import { createQuery, type QueryClient } from '@tanstack/solid-query'
import { api, getCurrentUserId } from '~/services/api'
import type { UserPurchaseStats } from '~/lib/derive-extra-boxes'

type StatsResponse = {
  stats: UserPurchaseStats | null
}

export const productPurchaseStatsQueryKey = ['product-purchase-stats'] as const

export const useProductPurchaseStats = (productId: () => number) => {
  return createQuery(() => ({
    queryKey: [...productPurchaseStatsQueryKey, productId()],
    queryFn: async () => {
      const userId = await getCurrentUserId()
      const response = await api.service
        .get(
          `store/users/${userId}/product/${productId()}/own-purchase-stats`,
          {
            searchParams: { range: 'today' },
          },
        )
        .json<StatsResponse>()
      return response.stats ?? { today_count: 0 }
    },
    enabled: productId() > 0,
  }))
}

/**
 * Optimistically bumps a product's `today_count` after a successful purchase
 * so `remaining` (and the buy button's disabled state) updates synchronously
 * instead of waiting on a refetch — same app-owned-cache approach as
 * `adjustActivityTokenBalance` and `markBoostingPackPurchased`.
 */
export function adjustProductPurchaseStats(
  queryClient: QueryClient,
  productId: number,
  delta: number,
) {
  queryClient.setQueryData<UserPurchaseStats>(
    [...productPurchaseStatsQueryKey, productId],
    (old) => ({ today_count: (old?.today_count ?? 0) + delta }),
  )
}
